import { useState } from "react";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import useTimestampColumnConfig from "../../main/config/column/default/TimestampColumnConfig";
import DataTableConfig from "../../main/config/dataTable/DataTableConfig";
import useDeleteRowActionConfig from "../../main/config/rowAction/default/DeleteRowActionConfig";
import DataTable, { useDataTable } from "../../main/DataTable";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import ColumnConfig from "../../main/config/column/ColumnConfig";
import { useLanguageContext } from "../../../../context/LanguageContext";
import useBasicColumnConfig from "../../main/config/column/default/BasicColumnConfig";
import useEditRowActionConfig from "../../main/config/rowAction/default/EditRowActionConfig";
import useVisitorEditRouteConfig from "../../../../routes/configs/visitors/VisitorEditRouteConfig";
import useCreateHeaderActionConfig from "../../main/config/headerAction/default/CreateHeaderActionConfig";
import useVisitorCreateRouteConfig from "../../../../routes/configs/visitors/VisitorCreateRouteConfig";
import RowActionConfig from "../../main/config/rowAction/RowActionConfig";
import {
  faAngleDown,
  faAngleUp,
  faCheck,
  faEnvelope,
  faPrint,
  faQuestion,
  faSignIn,
  faSignOut,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useAuthContext } from "../../../../context/AuthContext";
import Select from "../../../select/main/Select";
import { useLoadContext } from "../../../../context/LoadContext";
import useBooleanColumnConfig from "../../main/config/column/default/BooleanColumnConfig";
import useDeleteHeaderActionConfig from "../../main/config/headerAction/default/DeleteHeaderActionConfig";
import PrinterHelper from "../../../../helpers/PrinterHelper";
import PrintLabel from "../../../../pages/printers/printer/label_editor/label/PrintLabel";
import { useFrontofficeContext } from "../../../../context/FrontofficeContext";
import useCompanyColumnConfig from "../../main/config/column/default/CompanyColumnConfig";
import QuestionAnswers from "./question_answers/QuestionAnswers";
import useExportHeaderActionConfig from "../../main/config/headerAction/default/ExportHeaderActionConfig";
import useImportHeaderActionConfig from "../../main/config/headerAction/default/ImportHeaderActionConfig";
import { useTheme } from "styled-components";

function VisitorsDataTable() {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const loadContext = useLoadContext();
  const frontofficeContext = useFrontofficeContext();
  const { translate, translateEntity } = useLanguageContext();
  const [fields, setFields] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [printData, setPrintData] = useState(null);
  const [openedQuestionsRow, setOpenedQuestionsRow] = useState(null);
  const theme = useTheme();

  //Create the datatable config
  const config = new DataTableConfig("visitors");

  // Set the header html
  config.setHeaderHtml((controller) => {
    const today = new Date().toISOString().split("T")[0];
    const tomorrow = new Date(Date.now() + 86400000)
      .toISOString()
      .split("T")[0];

    const typeWhere = controller.filters.where.find((w) => w.in_type) ?? null;
    const currentType =
      typeWhere?.type_value ?? typeWhere?.values?.[0]?.value ?? "all";

    function onTypeChange(value) {
      if (loadContext.isLoading(config.getFetchKey())) return;

      const newWhere = controller.filters.where.filter((w) => !w.in_type);

      if (value !== "all") {
        const typeFilter = getTypeFilter(value, today, tomorrow);
        newWhere.push({ ...typeFilter, in_type: true });
      }

      controller.setFilters((current) => ({
        ...current,
        where: newWhere,
        page: 1,
      }));
    }

    return (
      <Select
        value={currentType}
        options={[
          { value: "all", label: translate("eva.main.general.all") },
          {
            value: "signed_in",
            label: translate("eva.main.visitor_statuses.signed_in"),
          },
          {
            value: "expected",
            label: translate("eva.main.visitor_statuses.expected"),
          },
          {
            value: "expected_today",
            label: translate("eva.main.visitor_statuses.expected_today"),
          },
          {
            value: "expected_tomorrow",
            label: translate("eva.main.visitor_statuses.expected_tomorrow"),
          },
        ]}
        sort={false}
        onChange={onTypeChange}
      />
    );
  });

  //Set the datatable fetch info
  config.setFetchRequest("visitors");
  config.setFetchParams({
    with_visitor_fields_data: true,
    with_visitor_visitor_type_data: true,
    with_visitor_employee_data: true,
    with_visitor_language_data: true,
    with_visitor_company_data: true,
    with_visitor_location_data: true,
    with_visitor_sign_in_terminal_data: true,
    with_visitor_sign_in_bc_terminal_data: true,
    with_visitor_question_answers_data: true,
    with_visitor_question_answer_question_data: true,
    with_visitor_question_answer_answer_data: true,
    with_visitor_parking_location_data: true,
    with_visitor_references_data: true,
  });

  //Set default order
  config.setOrderBy("id");
  config.setOrderDirection("asc");

  config.setRowStyle((row) => {
    // If this is the main registration of a recurring visitor, highlight the row
    if (row.occurrences_count > 1 && isExpanded(row, dataTable)) {
      return {
        backgroundColor: `rgba(${theme.colors.primary.darkestRGB}, ${isMainRegistration(row, dataTable) ? 0.5 : 0.33})`,
      };
    }
  });

  //Set the datatable columns
  config.setColumns([
    useCompanyColumnConfig(),
    useBasicColumnConfig("location.name"),
    useBasicColumnConfig("registration_code"),

    useBasicColumnConfig("identifier_string")
      .setData((row) => {
        return frontofficeContext.getVisitorIdentifierString(row);
      })
      .setSearchable(false)
      .setOrderable(false),

    useBasicColumnConfig("status.translation").setData((row) =>
      translate("eva.main.visitor_statuses." + row.status),
    ),

    useBasicColumnConfig("approval_status.translation")
      .setData((row) =>
        translate("eva.main.visitor_approval_statuses." + row.approval_status),
      )
      .setActive(false),

    useBasicColumnConfig("visitor_type.name.translation").setData((row) =>
      translateEntity(row.visitor_type, "label"),
    ),

    ...fields.map((field) =>
      new ColumnConfig()
        .setKey(
          `field_${field.id}.${
            field.type === "select" ? "option.translation" : "value"
          }`,
        )
        .setLabel(translateEntity(field, "label"))
        .setData((row) => getFieldColumnData(field, row))
        .setSearchable(field.type !== "boolean")
        .setActive(false),
    ),

    useBasicColumnConfig("email"),
    useBasicColumnConfig("language.label").setActive(false),
    useBasicColumnConfig("employee.name"),
    useBasicColumnConfig("license_plate").setActive(false),

    //The reference columns, which combine the values of all references of the visitor
    ...["reference", "client", "status", "type", "adr", "customs"].map((key) =>
      new ColumnConfig()
        .setKey(`references.${key}`)
        .setLabel(
          translate(`eva.main.validation.attributes.references.${key}`),
        )
        .setData((row) => getReferenceColumnData(row, key))
        .setSearchable(key !== "adr" && key !== "customs")
        .setOrderable(false)
        .setActive(false),
    ),
    
    useBasicColumnConfig("parking_location.name").setActive(false),
    useBooleanColumnConfig("returning"),
    useBasicColumnConfig("expected_date"),
    useBasicColumnConfig("expected_end_date"),
    useBasicColumnConfig("expected_time"),
    useBasicColumnConfig("sign_in_terminal.name"),
    useBasicColumnConfig("sign_in_bc_terminal.name").setActive(false),
    useBasicColumnConfig("signed_in_at"),
    useTimestampColumnConfig("created_at"),
    useTimestampColumnConfig("updated_at"),
  ]);

  //Set the datatable row actions
  config.setRowActions([
    new RowActionConfig()
      .setIcon(faCheck)
      .setDisabled(!authContext.hasPermission("visitors.approve"))
      .setHidden(
        (row) =>
          row.approval_status === "approved" ||
          !row.visitor_type?.requires_approval,
      )
      .setLoadingGroups((row) => `visitor_approve_${row.id}`)
      .setTitle(
        translate("eva.main.entities.states.approve", [
          { key: "entities", value: translate("eva.main.entities.visitor") },
        ]),
      )
      .setCallback(onApprove),

    new RowActionConfig()
      .setIcon(faXmark)
      .setDisabled(!authContext.hasPermission("visitors.approve"))
      .setHidden(
        (row) =>
          row.approval_status === "rejected" ||
          !row.visitor_type?.requires_approval,
      )
      .setLoadingGroups((row) => `visitor_reject_${row.id}`)
      .setTitle(
        translate("eva.main.entities.states.reject", [
          { key: "entities", value: translate("eva.main.entities.visitor") },
        ]),
      )
      .setCallback(onReject),

    new RowActionConfig()
      .setIcon(faSignIn)
      .setDisabled(!authContext.hasPermission("visitors.sign_in"))
      .setCallback(onSignIn)
      .setHidden((row) => row.signed_in_at)
      .setLoadingGroups((row) => `visitor_sign_in_${row.id}`)
      .setTitle(
        translate("eva.main.entities.states.sign_in", [
          { key: "entities", value: translate("eva.main.entities.visitor") },
        ]),
      ),

    new RowActionConfig()
      .setIcon(faSignOut)
      .setDisabled(!authContext.hasPermission("visitors.sign_out"))
      .setCallback(onSignOut)
      .setHidden((row) => !row.signed_in_at)
      .setLoadingGroups((row) => `visitor_sign_out_${row.id}`)
      .setTitle(
        translate("eva.main.entities.states.sign_out", [
          { key: "entities", value: translate("eva.main.entities.visitor") },
        ]),
      ),

    new RowActionConfig()
      .setIcon(faQuestion)
      .setTitle(translate("eva.main.entities.questions"))
      .setCallback((row) => setOpenedQuestionsRow(row))
      .setHidden((row) => row.question_answers.length === 0),

    new RowActionConfig()
      .setIcon(faPrint)
      .setDisabled(!authContext.hasPermission("printers.read"))
      .setCallback(onInitPrint)
      .setHidden((row) => printers.length === 0)
      .setLoadingGroups((row) => `visitor_print`)
      .setTitle(translate("eva.main.general.print"))
      .setOptions(
        printers.flatMap((printer) => [
          {
            value: printer.id,
            label:
              printer.name + " (" + translate("eva.main.general.print") + ")",
          },
          {
            value: printer.id + "_pdf",
            label: printer.name + " (PDF)",
          },
        ]),
      ),

    new RowActionConfig()
      .setIcon(faEnvelope)
      .setCallback(onSendMessages)
      .setLoadingGroups((row) => `visitor_send_messages_${row.id}`)
      .setTitle(
        translate("eva.main.entities.states.send", [
          { key: "entities", value: translate("eva.main.entities.messages") },
        ]),
      ),

    useEditRowActionConfig("visitor", useVisitorEditRouteConfig()),

    useDeleteRowActionConfig("visitor").setHidden((row) => row.signed_in_at),

    new RowActionConfig()
      .setIcon((row, controller) =>
        isExpanded(row, controller) ? faAngleDown : faAngleUp,
      )
      .setTitle(
        (row, controller) =>
          (isExpanded(row, controller)
            ? translate("eva.main.general.collapse")
            : translate("eva.main.general.expand")) +
          " (" +
          row.occurrences_count +
          ")",
      )
      .setLoadingGroups((row, controller) => `visitor_occurrences_${row.id}`)
      .setCallback((row, controller) => onExpand(row, controller))
      .setHidden(
        (row, controller) =>
          row.occurrences_count <= 1 || !isMainRegistration(row, controller),
      ),
  ]);

  //Set the datatable header actions
  config.setHeaderActions([
    useCreateHeaderActionConfig("visitor", useVisitorCreateRouteConfig()),
    useExportHeaderActionConfig("visitors"),
    useImportHeaderActionConfig("visitors"),
    useDeleteHeaderActionConfig("visitor"),
  ]);

  //Set the rows to be selectable
  config.setSelectableRows(true);
  config.setShowSavedFilters(true);

  //Create the datatable object
  const dataTable = useDataTable(config);

  /**
   * This effect will fetch the fields from the API
   */
  useCustomEffect(() => {
    fetchFields();
    fetchPrinters();
  });

  /**
   * This method will handle the print when the print data changes
   */
  useCustomEffect(() => {
    if (printData) {
      onPrint();
    }
  }, printData);

  /**
   * This method will fetch the fields from the API
   */
  async function fetchFields() {
    await callEvaApi(
      new EvaApiCall("fields")
        .setLoadingGroup(config.getFetchKey())
        .setParams({ with_field_options_data: true })
        .setAlertError(true)
        .setAlertSuccess(false)
        .setOnSuccess((response) => {
          setFields(response.data.data.records);
        }),
    );
  }

  /**
   * This method will fetch the printers from the API
   */
  async function fetchPrinters() {
    await callEvaApi(
      new EvaApiCall("printers")
        .setLoadingGroup(config.getFetchKey())
        .setParams({
          with_printer_label_items_data: true,
          with_printer_label_item_image_data: true,
        })
        .setAlertError(true)
        .setAlertSuccess(false)
        .setOnSuccess((response) => {
          setPrinters(response.data.data.records);
        }),
    );
  }

  /**
   * This method will return the column data for the given field and row
   * @param {object} field
   * @param {object} row
   * @returns {any}
   */
  function getFieldColumnData(field, row) {
    // Find the field data for the given field
    const fieldData = row.fields?.find((f) => f.field === field.id);
    if (!fieldData) {
      return "";
    }

    // If the field is a select, return the translation of the selected option
    if (field.type === "select") {
      const option = field.options.find(
        (o) => o.id === fieldData?.field_option,
      );
      return option ? translateEntity(option, "label") : "";
    }
    // If the field is a boolean, return the translation of the value
    else if (field.type === "boolean") {
      return fieldData?.value === "1"
        ? translate("eva.main.general.yes")
        : translate("eva.main.general.no");
    }

    // Otherwise, return the value of the field
    return fieldData?.value ?? "";
  }

  /**
   * This method will return the column data for the given reference key and row
   * @param {object} row
   * @param {string} key
   * @returns {string}
   */
  function getReferenceColumnData(row, key) {
    return (row.references ?? [])
      .map((reference) => {
        if (key === "adr" || key === "customs") {
          return reference[key]
            ? translate("eva.main.general.yes")
            : translate("eva.main.general.no");
        }
        return reference[key] ?? "";
      })
      .filter((value) => value !== "")
      .join(", ");
  }

  /**
   * This method will return the pre filter for the datatable
   * @returns {object[]}
   */
  function getTypeFilter(value, today, tomorrow) {
    switch (value) {
      case "signed_in":
        return { column: "status", values: [{ value: "signed_in" }] };
      case "expected":
        return { column: "status", values: [{ value: "expected" }] };
      case "expected_today":
        return {
          column: "expected_date",
          values: [{ value: today, operator: "=" }],
          type_value: "expected_today",
        };
      case "expected_tomorrow":
        return {
          column: "expected_date",
          values: [{ value: tomorrow, operator: "=" }],
          type_value: "expected_tomorrow",
        };
      default:
        return null;
    }
  }

  /**
   * This method will handle the sign in action
   * @param {object} row
   */
  async function onSignIn(row) {
    await callEvaApi(
      new EvaApiCall(`visitors/sign_in/${row.id}/simplified`)
        .setMethod("POST")
        .setLoadingGroup(`visitor_sign_in_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => dataTable.fetch()),
    );
  }

  /**
   * This method will handle the sign in action
   * @param {object} row
   */
  async function onSignOut(row) {
    await callEvaApi(
      new EvaApiCall(`visitors/sign_out/${row.id}/simplified`)
        .setMethod("POST")
        .setLoadingGroup(`visitor_sign_out_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => dataTable.fetch()),
    );
  }

  /**
   * This method will handle the approve action
   * @param {object} row
   */
  async function onApprove(row) {
    await callEvaApi(
      new EvaApiCall(`visitors/${row.id}/approve`)
        .setMethod("POST")
        .setLoadingGroup(`visitor_approve_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => dataTable.fetch()),
    );
  }

  /**
   * This method will handle the reject action
   * @param {object} row
   */
  async function onReject(row) {
    await callEvaApi(
      new EvaApiCall(`visitors/${row.id}/reject`)
        .setMethod("POST")
        .setLoadingGroup(`visitor_reject_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => dataTable.fetch()),
    );
  }

  /**
   * This method will handle init print action
   * @param {object} row
   * @param {DataTableConfig} dataTable
   * @param {int} printerId
   */
  async function onInitPrint(row, dataTable, printerId) {
    const printType = printerId.toString().endsWith("_pdf") ? "pdf" : "print";
    printerId = parseInt(printerId.toString().replace("_pdf", ""), 10);
    const printer = printers.find((p) => p.id === printerId);
    if (!printer) {
      return;
    }

    setPrintData({
      printerData: printer,
      printType: printType,
      visitorData: row,
    });
  }

  /**
   * This method will handle the actual printing
   */
  async function onPrint() {
    const loadTag = loadContext.enableLoader(`visitor_print`);

    try {
      //Get the element to print
      const element = document.getElementById(`[PRINT_LABEL_READONLY]`);

      //Determine if we need to generate a PDF
      let pdf = null;
      if (
        printData.printType === "pdf" ||
        printData.printerData.printer_device_id
      ) {
        pdf = await PrinterHelper.getPdfFromHtml(
          element,
          printData.printerData.label_width,
          printData.printerData.label_height,
          printData.printerData.rotate,
        );
      }

      //If the print type is PDF, open the PDF in a new tab
      if (printData.printType === "pdf") {
        const url = URL.createObjectURL(pdf.output);
        window.open(url, "_blank");
      }

      //If the print type is print and the printer has a printer device, send the PDF to the printer device
      if (
        printData.printType === "print" &&
        printData.printerData.printer_device_id
      ) {
        await callEvaApi(
          new EvaApiCall(
            `printer_devices/${printData.printerData.printer_device_id}/print`,
          )
            .setMethod("POST")
            .setData({
              file: pdf.output,
              width: pdf.pdfWidth,
              height: pdf.pdfHeight,
              dpi: printData.printerData.dpi,
            })
            .setAlertError(true)
            .setAlertSuccess(true),
        );
      }

      //If the print type is print and the printer does not have a printer device, generate ZPL and send it to the printer URL
      if (
        printData.printType === "print" &&
        !printData.printerData.printer_device_id
      ) {
        const zplCode = await PrinterHelper.getZplFromHtml(element);
        await PrinterHelper.printZpl(zplCode);
      }
    } finally {
      loadContext.disableLoader(loadTag);
      setPrintData(null);
    }
  }

  /**
   * This method will handle the send messages action
   * @param {object} row
   */
  async function onSendMessages(row) {
    await callEvaApi(
      new EvaApiCall(`visitors/${row.id}/messages/send`)
        .setMethod("POST")
        .setLoadingGroup(`visitor_send_messages_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(true),
    );
  }

  /**
   * This method will handle the expand action to show the occurrences of a visitor
   * @param {*} row
   * @param {DataTableConfig} controller
   * @returns
   */
  async function onExpand(row, controller) {
    const expandedRow = controller.data.records.find((r) =>
      isExpanded(r, controller),
    );
    if (expandedRow) {
      controller.setData((prevData) => {
        const newRecords = [...prevData.records];
        const currentRowIndex = newRecords.findIndex(
          (r) => r.id === expandedRow.id,
        );
        const beforeCurrentRow = newRecords.slice(0, currentRowIndex + 1);
        const afterCurrentRow = newRecords.slice(currentRowIndex + 1);

        const newRecordsWithoutOccurrences = afterCurrentRow.filter(
          (r) => r.registration_code !== expandedRow.registration_code,
        );

        return {
          ...prevData,
          records: [...beforeCurrentRow, ...newRecordsWithoutOccurrences],
        };
      });
    }

    if (expandedRow?.id === row.id) {
      return;
    }

    await callEvaApi(
      new EvaApiCall(`visitors/${row.id}/occurrences`)
        .setLoadingGroup(`visitor_occurrences_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(false)
        .setParams({
          with_visitor_fields_data: true,
          with_visitor_visitor_type_data: true,
          with_visitor_employee_data: true,
          with_visitor_language_data: true,
          with_visitor_company_data: true,
          with_visitor_location_data: true,
          with_visitor_sign_in_terminal_data: true,
          with_visitor_sign_in_bc_terminal_data: true,
          with_visitor_question_answers_data: true,
          with_visitor_question_answer_question_data: true,
          with_visitor_question_answer_answer_data: true,
          with_visitor_parking_location_data: true,
          with_visitor_references_data: true,
        })
        .setOnSuccess((response) => {
          const occurrences = response.data.data.records ?? response.data.data;

          // Update the controller data state and insert occurrences after the current row
          controller.setData((prevData) => {
            const newRecords = [...prevData.records];
            const currentRowIndex = newRecords.findIndex(
              (r) => r.id === row.id,
            );

            const beforeCurrentRow = newRecords.slice(0, currentRowIndex + 1);
            const afterCurrentRow = newRecords.slice(currentRowIndex + 1);

            return {
              ...prevData,
              records: [
                ...beforeCurrentRow,
                ...occurrences,
                ...afterCurrentRow,
              ],
            };
          });
        }),
    );
  }

  /**
   * This method will check if the given row is expanded in the given controller
   * @param {*} row
   * @param {*} controller
   * @returns
   */
  function isExpanded(row, controller) {
    return controller.data.records.find(
      (r) => r.registration_code === row.registration_code && row.id !== r.id,
    );
  }

  /**
   * Check if the given row has the lowest id among the records with the same registration_code
   * @param {*} row
   * @param {*} controller
   * @returns
   */
  function isMainRegistration(row, controller) {
    const sameRegistrationRows = controller.data.records.filter(
      (r) => r.registration_code === row.registration_code,
    );
    const minId = Math.min(...sameRegistrationRows.map((r) => r.id));
    return row.id === minId;
  }

  return (
    <>
      <DataTable controller={dataTable} />

      {printData && (
        <PrintLabel
          printerData={printData?.printerData}
          visitorData={printData?.visitorData}
          fields={fields}
          readOnly={true}
        />
      )}

      {openedQuestionsRow && (
        <QuestionAnswers
          questionAnswers={openedQuestionsRow.question_answers}
          onClose={() => setOpenedQuestionsRow(null)}
        />
      )}
    </>
  );
}

export default VisitorsDataTable;
